import { Platform, Linking } from 'react-native';
import { supabase } from '@services/supabase/supabaseClient';
import { User } from '@/types/auth.types';

/**
 * Get redirect URL based on platform
 */
const getRedirectURL = (): string => {
  if (Platform.OS === 'web') {
    if (typeof window !== 'undefined') {
      return `${window.location.origin}/auth/callback`;
    }
    return 'http://localhost:19006/auth/callback';
  }
  // Mobile deep link
  return 'tickervibes://auth/callback';
};

/**
 * Convert Supabase user to our User type
 */
const mapSupabaseUser = (supabaseUser: any): User => {
  return {
    id: supabaseUser.id,
    email: supabaseUser.email || '',
    name: supabaseUser.user_metadata?.name || supabaseUser.email?.split('@')[0] || '',
    avatar_url: supabaseUser.user_metadata?.avatar_url || null,
    phone_number: supabaseUser.phone || null,
    created_at: supabaseUser.created_at,
    updated_at: supabaseUser.updated_at || supabaseUser.created_at,
  };
};

/**
 * Sign in with Google
 */
export const signInWithGoogle = async (): Promise<{
  success: boolean;
  user?: User;
  error?: string;
}> => {
  try {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: getRedirectURL(),
        // Ensure full-page redirect on web (more reliable on mobile browsers)
        skipBrowserRedirect: false,
        queryParams: {
          // Force Google to show account picker every time
          // This ensures users explicitly choose which account to use
          prompt: 'select_account',
          access_type: 'offline', // Get refresh token for better session management
        },
      },
    });

    if (error) {
      console.error('Google sign in error:', error);
      return {
        success: false,
        error: error.message,
      };
    }

    // OAuth flow redirects, actual user is returned via callback
    return {
      success: true,
    };
  } catch (error: any) {
    console.error('Google sign in error:', error);
    return {
      success: false,
      error: error.message || 'Google sign in failed',
    };
  }
};

/**
 * Handle OAuth callback (call this in AuthCallbackScreen)
 */
export const handleOAuthCallback = async (opts?: { url?: string; code?: string }): Promise<{
  success: boolean;
  user?: User;
  error?: string;
}> => {
  try {
    // Web: Supabase automatically handles the OAuth callback via detectSessionInUrl
    if (Platform.OS === 'web') {
      // First try to get a session (Supabase may have auto-exchanged already)
      let { data, error } = await supabase.auth.getSession();
      if (error) {
        console.error('OAuth callback error (web):', error);
        return { success: false, error: error.message };
      }

      // If no session yet, try manual exchange using the current URL's code
      if (!data.session?.user) {
        try {
          const href = typeof window !== 'undefined' ? window.location.href : '';
          let code: string | undefined;
          if (href) {
            try {
              const parsed = new URL(href);
              code = parsed.searchParams.get('code') || undefined;
            } catch {
              const match = href.match(/[?&]code=([^&]+)/);
              code = match ? decodeURIComponent(match[1]) : undefined;
            }
          }

          if (code) {
            const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code as any);
            if (exchangeError) {
              console.error('Web code exchange error:', exchangeError);
              return { success: false, error: exchangeError.message };
            }

            // Refresh session data after exchange
            const res = await supabase.auth.getSession();
            data = res.data;
            error = res.error;
            if (error) {
              console.error('Get session after web exchange error:', error);
              return { success: false, error: error.message };
            }

            // Clean URL (remove code param) to avoid re-processing on refresh
            try {
              if (typeof window !== 'undefined') {
                const url = new URL(window.location.href);
                url.searchParams.delete('code');
                url.searchParams.delete('state');
                window.history.replaceState({}, document.title, url.toString());
              }
            } catch {}
          }
        } catch (e) {
          console.warn('Manual web exchange attempt failed:', e);
        }
      }

      if (!data.session?.user) {
        return { success: false, error: 'No session found' };
      }
      return { success: true, user: mapSupabaseUser(data.session.user) };
    }

    // Native: Manually exchange the auth code for a session via PKCE
    let url = opts?.url || (await Linking.getInitialURL()) || null;

    let code = opts?.code;
    if (!code) {
      if (!url) {
        return { success: false, error: 'No callback URL received' };
      }
      try {
        const parsed = new URL(url);
        code = parsed.searchParams.get('code') || undefined;
      } catch {
        const match = url.match(/[?&]code=([^&]+)/);
        code = match ? decodeURIComponent(match[1]) : undefined;
      }
    }

    if (!code) {
      return { success: false, error: 'No authorization code in callback URL' };
    }

    // Exchange the auth code for a session (Supabase stores session automatically)
    const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code as any);
    if (exchangeError) {
      console.error('OAuth code exchange error (native):', exchangeError);
      return { success: false, error: exchangeError.message };
    }

    // Confirm session and return user
    const { data: sessionData, error: getSessionError } = await supabase.auth.getSession();
    if (getSessionError) {
      console.error('Get session after exchange error (native):', getSessionError);
      return { success: false, error: getSessionError.message };
    }
    if (!sessionData.session?.user) {
      return { success: false, error: 'No session after exchange' };
    }

    return { success: true, user: mapSupabaseUser(sessionData.session.user) };
  } catch (error: any) {
    console.error('OAuth callback error:', error);
    return {
      success: false,
      error: error.message || 'OAuth callback failed',
    };
  }
};

/**
 * Logout current user
 */
export const logout = async (): Promise<void> => {
  try {
    // Use 'global' scope to ensure session is cleared everywhere
    await supabase.auth.signOut({ scope: 'global' });
  } catch (error) {
    console.error('Logout error:', error);
    throw error;
  }
};

/**
 * Get current authenticated user
 */
export const getCurrentUser = async (): Promise<User | null> => {
  try {
    const { data, error } = await supabase.auth.getUser();

    if (error) {
      console.error('Get user error:', error);
      return null;
    }

    if (!data.user) {
      return null;
    }

    return mapSupabaseUser(data.user);
  } catch (error) {
    console.error('Get current user error:', error);
    return null;
  }
};
